import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Settings, Clock } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { IMAGE_STYLE_PRESETS } from '../../constants/image-generation';
import type { ImageStylePreset, ImageQualityPreset, ImageModelId } from '../../../core/types/image-generation.types';

const QUALITY_OPTIONS: { value: string; label: string }[] = [
  { value: '512', label: '512' },
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

const MODEL_OPTIONS_BASE: { value: string; label: string }[] = [
  { value: 'nano-banana-pro', label: 'Nano Banana Pro' },
  { value: 'nano-banana-2', label: 'Nano Banana 2' },
  { value: 'soul-2.0', label: 'Soul 2.0' },
  { value: 'seedream-5.0', label: 'Seedream 5.0' },
  { value: 'flux-kontext', label: 'Flux Kontext' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
];

interface DropdownProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const CompactDropdown: React.FC<DropdownProps> = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-300 hover:border-zinc-500 transition-colors"
      >
        <span className="text-zinc-500">{label}:</span>
        <span className="font-medium">{selectedLabel}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-[1000] bg-zinc-800 border border-zinc-600 rounded shadow-lg py-1 min-w-[120px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-zinc-700 transition-colors
                ${opt.value === value ? 'text-blue-400 bg-zinc-700/50' : 'text-zinc-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const SlotGenerationOptions: React.FC = () => {
  const style = useSlotAssetGeneratorStore((s) => s.imageSettings.style);
  const quality = useSlotAssetGeneratorStore((s) => s.imageSettings.qualityPreset);
  const model = useSlotAssetGeneratorStore((s) => s.imageSettings.model);
  const customStyleText = useSlotAssetGeneratorStore((s) => s.imageSettings.customStyleText ?? '');
  const updateImageSettings = useSlotAssetGeneratorStore((s) => s.updateImageSettings);
  const setQualityPreset = useSlotAssetGeneratorStore((s) => s.setQualityPreset);
  const toggleSettings = useSlotAssetGeneratorStore((s) => s.toggleSettings);
  const historyPanelOpen = useSlotAssetGeneratorStore((s) => s.historyPanelOpen);
  const toggleHistoryPanel = useSlotAssetGeneratorStore((s) => s.toggleHistoryPanel);
  const historyCount = useSlotAssetGeneratorStore((s) => s.history.length);

  const handleStyleChange = useCallback((v: string) => {
    updateImageSettings({ style: v as ImageStylePreset });
  }, [updateImageSettings]);

  const handleQualityChange = useCallback((v: string) => {
    setQualityPreset(v);
  }, [setQualityPreset]);

  const handleModelChange = useCallback((v: string) => {
    updateImageSettings({ model: v as ImageModelId });
  }, [updateImageSettings]);

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800 flex-shrink-0 flex-wrap">
      <CompactDropdown
        label="Style"
        value={style}
        options={IMAGE_STYLE_PRESETS}
        onChange={handleStyleChange}
      />
      {style === 'custom' && (
        <input
          type="text"
          value={customStyleText}
          onChange={(e) => updateImageSettings({ customStyleText: e.target.value })}
          placeholder="Describe your style..."
          className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500 min-w-[120px]"
        />
      )}
      <CompactDropdown
        label="Quality"
        value={quality}
        options={QUALITY_OPTIONS}
        onChange={handleQualityChange}
      />
      <CompactDropdown
        label="Model"
        value={model}
        options={MODEL_OPTIONS_BASE}
        onChange={handleModelChange}
      />
      <div className="flex-1" />
      <button
        onClick={toggleSettings}
        className="p-1.5 text-zinc-500 hover:text-zinc-300 rounded hover:bg-zinc-700 transition-colors"
        title="API Key Settings"
      >
        <Settings size={14} />
      </button>
      <button
        onClick={toggleHistoryPanel}
        className={`flex items-center gap-1 p-1.5 rounded transition-colors ${
          historyPanelOpen
            ? 'text-blue-400 bg-blue-900/20 hover:bg-blue-900/30'
            : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
        }`}
        title="Toggle history panel"
      >
        <Clock size={14} />
        {historyCount > 0 && (
          <span className="text-[9px] bg-zinc-600 text-zinc-300 px-1 rounded-full min-w-[14px] text-center leading-tight">
            {historyCount}
          </span>
        )}
      </button>
    </div>
  );
};
